import { format } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'
import { cn } from 'src/utils/style'

interface ExpenseTimestampsProps {
  created?: string | null
  lastEdited?: string | null
  className?: string
}

/**
 * Read-only display of an expense's audit timestamps (Created / Updated).
 * Display-only — not form inputs and not part of submitted data. Renders
 * nothing when no timestamps are present (e.g. a brand-new expense on create).
 */
export const ExpenseTimestamps = ({ created, lastEdited, className }: ExpenseTimestampsProps) => {
  const { t, i18n } = useTranslation()

  if (!created && !lastEdited) return null

  const locale = i18n.language?.toLowerCase().startsWith('es') ? es : enUS
  const formatTimestamp = (iso: string) => format(new Date(iso), 'PPpp', { locale })

  return (
    <dl className={cn(['space-y-2 text-sm', className])} data-testid="expense-timestamps">
      {created && (
        <div className="flex justify-between gap-2">
          <dt className="text-text-placeholder">{t('expenseForm.createdTimestampLabel')}</dt>
          <dd className="text-gray-700" data-testid="expense-created-value">
            {formatTimestamp(created)}
          </dd>
        </div>
      )}
      {lastEdited && (
        <div className="flex justify-between gap-2">
          <dt className="text-text-placeholder">{t('expenseForm.updatedTimestampLabel')}</dt>
          <dd className="text-gray-700" data-testid="expense-updated-value">
            {formatTimestamp(lastEdited)}
          </dd>
        </div>
      )}
    </dl>
  )
}
